import { useEffect, useState } from "react";
import { Building2, CalendarDays, Mic2, CheckCircle2, Store } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { GENRES, STATES } from "../lib/constants";
import { useTerms } from "../lib/useTerms";
import { RichTextEditor } from "../components/RichTextEditor";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-rose-500 text-sm";
const Field = ({ label, ...p }) => (
  <label className="block"><span className="text-xs text-zinc-400">{label}</span><input className={inp + " mt-1"} {...p} /></label>
);
const GenreSelect = ({ value, onChange, opts = GENRES, ...p }) => (
  <label className="block"><span className="text-xs text-zinc-400">Genre</span>
    <select className={inp + " mt-1"} value={value} onChange={onChange} {...p}>
      <option value="">Select genre</option>{opts.map((g) => <option key={g} value={g}>{g}</option>)}
    </select>
  </label>
);
const Area = ({ label, ...p }) => (
  <label className="block sm:col-span-2"><span className="text-xs text-zinc-400">{label}</span><textarea rows={4} className={inp + " mt-1"} {...p} /></label>
);
const RichArea = ({ label, value, onChange, testid }) => (
  <div className="block sm:col-span-2"><span className="text-xs text-zinc-400">{label}</span><div className="mt-1"><RichTextEditor value={value} onChange={onChange} placeholder={`Write the ${(label || "").toLowerCase()}…`} testid={testid} /></div></div>
);

function useForm(initial) {
  const [d, setD] = useState(initial);
  return [d, (k, v) => setD((s) => ({ ...s, [k]: v }))];
}

function VenueForm({ onDone }) {
  const [step, setStep] = useState(1);
  const venueTypes = useTerms("venue", "venue_type", ["Live music", "Bar", "Restaurant", "Beach venue", "Theater", "Festival space", "Event hall"]);
  const states = useTerms("venue", "state", STATES);
  const [d, set] = useForm({ name: "", venue_type: "Live music", address: "", city: "", state: "Florida", phone: "", email: "", website: "", description: "", hours: "", hero_image: "", logo: "", capacity: "" });
  const submit = async () => {
    try { await api.post("/submissions", { submission_type: "venue", data: d }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="venue-form">
      {step === 1 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Venue name *" value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="vf-name" />
          <label className="block"><span className="text-xs text-zinc-400">Venue type</span><select className={inp + " mt-1"} value={d.venue_type} onChange={(e) => set("venue_type", e.target.value)}>{["Live music", "Bar", "Restaurant", "Beach venue", "Theater", "Festival space", "Event hall"].map((x) => <option key={x}>{x}</option>)}</select></label>
          <Field label="Address" value={d.address} onChange={(e) => set("address", e.target.value)} />
          <Field label="City" value={d.city} onChange={(e) => set("city", e.target.value)} data-testid="vf-city" />
          <label className="block"><span className="text-xs text-zinc-400">State</span><select className={inp + " mt-1"} value={d.state} onChange={(e) => set("state", e.target.value)}>{STATES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <Field label="Capacity" value={d.capacity} onChange={(e) => set("capacity", e.target.value)} />
          <RichArea label="Description" value={d.description} onChange={(v) => set("description", v)} testid="vf-description" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone" value={d.phone} onChange={(e) => set("phone", e.target.value)} />
          <Field label="Email" value={d.email} onChange={(e) => set("email", e.target.value)} />
          <Field label="Website" value={d.website} onChange={(e) => set("website", e.target.value)} />
          <Field label="Hours" value={d.hours} onChange={(e) => set("hours", e.target.value)} />
          <Field label="Hero image URL" value={d.hero_image} onChange={(e) => set("hero_image", e.target.value)} />
          <Field label="Logo URL" value={d.logo} onChange={(e) => set("logo", e.target.value)} />
        </div>
      )}
      <StepNav step={step} setStep={setStep} submit={submit} canNext={!!d.name} />
    </div>
  );
}

function ArtistForm({ onDone }) {
  const [step, setStep] = useState(1);
  const genres = useTerms("artist", "genre", GENRES);
  const perfTypes = useTerms("artist", "performance_type", ["Band", "Solo artist", "DJ", "Comedy"]);
  const states = useTerms("artist", "state", STATES);
  const [d, set] = useForm({ name: "", genre: "", home_city: "", home_state: "Florida", performance_type: "Band", bio: "", tagline: "", booking_email: "", booking_phone: "", website: "", booking_available: true, hero_image: "", profile_image: "" });
  const submit = async () => {
    try { await api.post("/submissions", { submission_type: "artist", data: d }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="artist-form">
      {step === 1 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Artist / band name *" value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="af-name" />
          <GenreSelect value={d.genre} onChange={(e) => set("genre", e.target.value)} opts={genres} data-testid="af-genre" />
          <Field label="Home city" value={d.home_city} onChange={(e) => set("home_city", e.target.value)} />
          <label className="block"><span className="text-xs text-zinc-400">State</span><select className={inp + " mt-1"} value={d.home_state} onChange={(e) => set("home_state", e.target.value)}>{states.map((s) => <option key={s}>{s}</option>)}</select></label>
          <label className="block"><span className="text-xs text-zinc-400">Performance type</span><select className={inp + " mt-1"} value={d.performance_type} onChange={(e) => set("performance_type", e.target.value)}>{perfTypes.map((x) => <option key={x}>{x}</option>)}</select></label>
          <Field label="Tagline" value={d.tagline} onChange={(e) => set("tagline", e.target.value)} />
          <RichArea label="Bio" value={d.bio} onChange={(v) => set("bio", v)} testid="af-bio" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Booking email" value={d.booking_email} onChange={(e) => set("booking_email", e.target.value)} />
          <Field label="Booking phone" value={d.booking_phone} onChange={(e) => set("booking_phone", e.target.value)} />
          <Field label="Website" value={d.website} onChange={(e) => set("website", e.target.value)} />
          <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={d.booking_available} onChange={(e) => set("booking_available", e.target.checked)} /> Available for booking</label>
          <Field label="Hero image URL" value={d.hero_image} onChange={(e) => set("hero_image", e.target.value)} />
          <Field label="Profile image URL" value={d.profile_image} onChange={(e) => set("profile_image", e.target.value)} />
        </div>
      )}
      <StepNav step={step} setStep={setStep} submit={submit} canNext={!!d.name} />
    </div>
  );
}

function EventForm({ onDone }) {
  const [step, setStep] = useState(1);
  const [venues, setVenues] = useState([]);
  const [artists, setArtists] = useState([]);
  const eventGenres = useTerms("event", "genre", GENRES);
  const eventTypes = useTerms("event", "event_type", ["Concert", "Festival"]);
  const artistGenres = useTerms("artist", "genre", GENRES);
  const states = useTerms("event", "state", STATES);
  const [newVenueMode, setNewVenueMode] = useState(false);
  const [newVenue, setNewVenue] = useForm({ name: "", city: "", state: "Florida", address: "" });
  const [artistIds, setArtistIds] = useState([]);
  const [newArtists, setNewArtists] = useState([]);
  const [pickArtist, setPickArtist] = useState("");
  const [newArtistName, setNewArtistName] = useState("");
  const [newArtistGenre, setNewArtistGenre] = useState("");
  const [d, set] = useForm({ title: "", event_date: "", start_time: "20:00", end_time: "", venue_id: "", genre: "", event_type: "Concert", ticket_price: "0", ticket_link: "", age_restriction: "21+", description: "", promoter_name: "", promoter_contact: "", event_image: "" });
  useEffect(() => { api.get("/venues").then((r) => setVenues(r.data)); api.get("/artists").then((r) => setArtists(r.data)); }, []);

  const addArtist = () => { if (pickArtist && !artistIds.includes(pickArtist)) { setArtistIds([...artistIds, pickArtist]); setPickArtist(""); } };
  const addNewArtist = () => { if (newArtistName.trim()) { setNewArtists([...newArtists, { name: newArtistName.trim(), genre: newArtistGenre.trim(), home_state: "Florida" }]); setNewArtistName(""); setNewArtistGenre(""); } };
  const artistName = (id) => (artists.find((a) => a.id === id) || {}).name || id;

  const submit = async () => {
    const payload = { ...d, artist_ids: artistIds, new_artists: newArtists };
    if (newVenueMode) { payload.venue_id = ""; payload.new_venue = newVenue; }
    try { await api.post("/submissions", { submission_type: "event", data: payload }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="event-form">
      {step === 1 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Event name *" value={d.title} onChange={(e) => set("title", e.target.value)} data-testid="ef-title" />
          <Field label="Date" type="date" value={d.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="ef-date" />
          <Field label="Start time" type="time" value={d.start_time} onChange={(e) => set("start_time", e.target.value)} />
          <Field label="End time" type="time" value={d.end_time} onChange={(e) => set("end_time", e.target.value)} />
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400">Venue</span>
              <button type="button" onClick={() => setNewVenueMode(!newVenueMode)} className="text-xs text-rose-400" data-testid="ef-toggle-new-venue">{newVenueMode ? "← Pick existing venue" : "+ Can't find it? Add a new venue"}</button>
            </div>
            {!newVenueMode ? (
              <select className={inp + " mt-1"} value={d.venue_id} onChange={(e) => set("venue_id", e.target.value)} data-testid="ef-venue"><option value="">Select a venue</option>{venues.map((v) => <option key={v.id} value={v.id}>{v.name} — {v.city}</option>)}</select>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 mt-1 bg-[#0a0a0a] border border-white/10 rounded-lg p-3" data-testid="ef-new-venue">
                <input className={inp} placeholder="New venue name *" value={newVenue.name} onChange={(e) => setNewVenue("name", e.target.value)} data-testid="ef-nv-name" />
                <input className={inp} placeholder="Address" value={newVenue.address} onChange={(e) => setNewVenue("address", e.target.value)} />
                <input className={inp} placeholder="City" value={newVenue.city} onChange={(e) => setNewVenue("city", e.target.value)} data-testid="ef-nv-city" />
                <select className={inp} value={newVenue.state} onChange={(e) => setNewVenue("state", e.target.value)}>{states.map((s) => <option key={s}>{s}</option>)}</select>
                <p className="sm:col-span-2 text-xs text-zinc-500">This venue will be created and published when an admin approves the event.</p>
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <span className="text-xs text-zinc-400">Lineup / Artists</span>
            {(artistIds.length > 0 || newArtists.length > 0) && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="ef-lineup-chips">
                {artistIds.map((id) => (
                  <span key={id} className="flex items-center gap-1 bg-rose-500/15 text-rose-200 rounded-full pl-3 pr-1.5 py-1 text-sm">{artistName(id)}<button type="button" onClick={() => setArtistIds(artistIds.filter((x) => x !== id))} className="hover:text-white">×</button></span>
                ))}
                {newArtists.map((na, i) => (
                  <span key={`n${i}`} className="flex items-center gap-1 bg-amber-500/15 text-amber-200 rounded-full pl-3 pr-1.5 py-1 text-sm">{na.name} (new)<button type="button" onClick={() => setNewArtists(newArtists.filter((_, j) => j !== i))} className="hover:text-white">×</button></span>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <select className={inp} value={pickArtist} onChange={(e) => setPickArtist(e.target.value)} data-testid="ef-artist-select"><option value="">Select an existing artist</option>{artists.filter((a) => !artistIds.includes(a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
              <button type="button" onClick={addArtist} className="bg-white/10 hover:bg-white/20 px-4 rounded-lg text-sm shrink-0" data-testid="ef-add-artist">Add</button>
            </div>
            <div className="flex gap-2 mt-2">
              <input className={inp} placeholder="Or add a new artist by name" value={newArtistName} onChange={(e) => setNewArtistName(e.target.value)} data-testid="ef-new-artist-name" />
              <select className={inp + " max-w-[40%]"} value={newArtistGenre} onChange={(e) => setNewArtistGenre(e.target.value)} data-testid="ef-new-artist-genre"><option value="">Genre</option>{artistGenres.map((g) => <option key={g} value={g}>{g}</option>)}</select>
              <button type="button" onClick={addNewArtist} className="bg-white/10 hover:bg-white/20 px-4 rounded-lg text-sm shrink-0" data-testid="ef-add-new-artist">+ New</button>
            </div>
          </div>

          <RichArea label="Description" value={d.description} onChange={(v) => set("description", v)} testid="ef-description" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <GenreSelect value={d.genre} onChange={(e) => set("genre", e.target.value)} data-testid="ef-genre" />
          <label className="block"><span className="text-xs text-zinc-400">Type</span><select className={inp + " mt-1"} value={d.event_type} onChange={(e) => set("event_type", e.target.value)}>{eventTypes.map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
          <Field label="Ticket price ($)" value={d.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} />
          <Field label="Ticket link" value={d.ticket_link} onChange={(e) => set("ticket_link", e.target.value)} />
          <Field label="Age restriction" value={d.age_restriction} onChange={(e) => set("age_restriction", e.target.value)} />
          <Field label="Promoter name" value={d.promoter_name} onChange={(e) => set("promoter_name", e.target.value)} />
          <Field label="Promoter contact" value={d.promoter_contact} onChange={(e) => set("promoter_contact", e.target.value)} />
          <Field label="Flyer image URL" value={d.event_image} onChange={(e) => set("event_image", e.target.value)} />
        </div>
      )}
      <StepNav step={step} setStep={setStep} submit={submit} canNext={!!d.title} />
    </div>
  );
}

function BusinessForm({ onDone }) {
  const [step, setStep] = useState(1);
  const bizTypes = useTerms("business", "business_type", ["Hospitality Group", "Tour Operator", "Entertainment Company", "Restaurant Group", "Rental Company", "Attraction Operator", "Retail", "Other"]);
  const [d, set] = useForm({ name: "", business_type: "Tour Operator", address: "", city: "", state: "Florida", zip: "", phone: "", email: "", website: "", description: "", hours: "", hero_image: "", logo: "" });
  const submit = async () => {
    try { await api.post("/businesses", d); onDone(); }
    catch (e) { toast.error(e?.response?.status === 401 ? "Please log in to submit a business." : errMsg(e)); }
  };
  return (
    <div data-testid="business-form">
      {step === 1 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name *" value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="bf-name" />
          <label className="block"><span className="text-xs text-zinc-400">Business type</span><select className={inp + " mt-1"} value={d.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="bf-type">{bizTypes.map((x) => <option key={x}>{x}</option>)}</select></label>
          <Field label="Address" value={d.address} onChange={(e) => set("address", e.target.value)} />
          <Field label="City" value={d.city} onChange={(e) => set("city", e.target.value)} data-testid="bf-city" />
          <label className="block"><span className="text-xs text-zinc-400">State</span><select className={inp + " mt-1"} value={d.state} onChange={(e) => set("state", e.target.value)}>{STATES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <Field label="ZIP" value={d.zip} onChange={(e) => set("zip", e.target.value)} />
          <RichArea label="Description" value={d.description} onChange={(v) => set("description", v)} testid="bf-description" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone" value={d.phone} onChange={(e) => set("phone", e.target.value)} data-testid="bf-phone" />
          <Field label="Email" value={d.email} onChange={(e) => set("email", e.target.value)} data-testid="bf-email" />
          <Field label="Website" value={d.website} onChange={(e) => set("website", e.target.value)} />
          <Field label="Hours" value={d.hours} onChange={(e) => set("hours", e.target.value)} />
          <Field label="Hero image URL" value={d.hero_image} onChange={(e) => set("hero_image", e.target.value)} />
          <Field label="Logo URL" value={d.logo} onChange={(e) => set("logo", e.target.value)} />
        </div>
      )}
      <StepNav step={step} setStep={setStep} submit={submit} canNext={!!d.name} />
    </div>
  );
}

function StepNav({ step, setStep, submit, canNext }) {
  return (
    <div className="flex justify-between items-center mt-8">
      <span className="text-xs text-zinc-500">Step {step} of 2</span>
      <div className="flex gap-3">
        {step > 1 && <button onClick={() => setStep(step - 1)} className="border border-white/20 px-5 py-2.5 rounded-lg text-sm" data-testid="form-back">Back</button>}
        {step < 2 ? <button disabled={!canNext} onClick={() => setStep(step + 1)} className="bg-rose-500 hover:bg-rose-600 px-6 py-2.5 rounded-lg font-bold text-sm disabled:opacity-40" data-testid="form-next">Next</button>
          : <button onClick={submit} className="bg-rose-500 hover:bg-rose-600 px-6 py-2.5 rounded-lg font-bold text-sm" data-testid="form-submit">Submit for approval</button>}
      </div>
    </div>
  );
}

export default function Submit() {
  const [type, setType] = useState(null);
  const [done, setDone] = useState(false);

  if (done) return (
    <div className="py-32 text-center px-6" data-testid="submit-success">
      <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={56} />
      <h1 className="font-display font-bold text-3xl">Submitted!</h1>
      <p className="text-zinc-400 mt-3 max-w-md mx-auto">Your submission is pending review. Our team will approve it shortly and it'll go live on Gulf Coast VIP.</p>
      <button onClick={() => { setDone(false); setType(null); }} className="mt-8 bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full" data-testid="submit-another">Submit another</button>
    </div>
  );

  const opts = [
    { v: "venue", l: "Submit a Venue", Icon: Building2, d: "List your bar, club, theater or beach venue" },
    { v: "event", l: "Submit an Event", Icon: CalendarDays, d: "Promote a concert, festival or show" },
    { v: "artist", l: "Submit an Artist", Icon: Mic2, d: "Add your band, DJ or solo act" },
    { v: "business", l: "Submit a Business", Icon: Store, d: "List your company — link it to attractions & activities, post deals & jobs" },
  ];

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 max-w-5xl mx-auto" data-testid="submit-page">
      <p className="overline mb-2">Get listed for free</p>
      <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-3">Submit to the Directory</h1>
      <p className="text-zinc-400 mb-10">All submissions are reviewed before going live.</p>
      {!type ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {opts.map((o) => (
            <button key={o.v} onClick={() => setType(o.v)} className="text-left bg-[#111] border border-white/10 rounded-2xl p-6 card-hover" data-testid={`submit-${o.v}`}>
              <o.Icon className="text-rose-400 mb-4" size={28} />
              <h3 className="font-display font-bold text-xl">{o.l}</h3>
              <p className="text-sm text-zinc-400 mt-2">{o.d}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-[#111] border border-white/10 rounded-2xl p-6 md:p-8">
          <button onClick={() => setType(null)} className="text-sm text-zinc-400 mb-6">← Choose different type</button>
          {type === "venue" && <VenueForm onDone={() => setDone(true)} />}
          {type === "artist" && <ArtistForm onDone={() => setDone(true)} />}
          {type === "event" && <EventForm onDone={() => setDone(true)} />}
          {type === "business" && <BusinessForm onDone={() => setDone(true)} />}
        </div>
      )}
    </div>
  );
}
